import { Alert, Button, Checkbox, Form, Input } from "antd";
import React, { useContext, useMemo } from "react";
import AuthContext from "../../context/AuthContext";
import { ButtonQuickbooks } from "../quickbooks";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { GoogleLoginButton } from "../google";
import companyLogo from "../../utils/company/assets/company_logo.svg";

interface LoginValues {
  username: string;
  password: string;
  remember: boolean;
}

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="m22 7-10 6L2 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="m12 5 7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const LoginForm = () => {
  const {
    onLogin,
    authLoading,
    authError,
    handleLoginQuickbooksSSO,
    openCloseModalResetPassword,
  } = useContext(AuthContext);
  const [form] = Form.useForm<LoginValues>();

  const initialRemember = useMemo(
    () => localStorage.getItem("collectpro_keep_signed_in") !== "false",
    []
  );

  const handleFinish = ({ username, password, remember }: LoginValues) => {
    localStorage.setItem("collectpro_keep_signed_in", remember ? "true" : "false");
    onLogin(username, password, remember);
  };

  const goToDemo = () => {
    window.location.replace("https://demo.collectpro.quattroapps.app");
  };

  return (
    <>
      <div className="login__top-link">
        Don&apos;t have an account?{" "}
        <button type="button" onClick={handleLoginQuickbooksSSO}>
          Create one
        </button>
      </div>

      <div className="login__panel">
        <img src={companyLogo} alt="CollectPRO" className="login__client-logo" />
        <h2 className="login__title">Welcome back</h2>
        <p className="login__subtitle">Sign in to manage your A/R workflows.</p>

        <Form<LoginValues>
          form={form}
          layout="vertical"
          requiredMark
          initialValues={{ remember: initialRemember }}
          onFinish={handleFinish}
          autoComplete="on"
          className="login__form"
        >
          <Form.Item
            label="Username or email"
            name="username"
            className="login__form-item"
            rules={[
              {
                required: true,
                message: "Please enter your username or email",
              },
            ]}
          >
            <Input
              prefix={<MailIcon />}
              placeholder="you@company.com"
              autoFocus
              autoComplete="username"
              aria-required="true"
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            className="login__form-item"
            rules={[
              {
                required: true,
                message: "Please enter your password",
              },
            ]}
          >
            <Input.Password
              prefix={<LockIcon />}
              placeholder="Enter password"
              autoComplete="current-password"
              aria-required="true"
            />
          </Form.Item>

          <div className="login__form-row">
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox>Keep me signed in</Checkbox>
            </Form.Item>
            <button
              type="button"
              className="login__forgot"
              onClick={() => openCloseModalResetPassword(true)}
            >
              Forgot password?
            </button>
          </div>

          {authError.status && (
            <Alert
              type="error"
              showIcon
              message={authError.message}
              className="login__alert"
            />
          )}

          <Form.Item className="login__submit-item">
            <Button
              htmlType="submit"
              type="primary"
              block
              loading={authLoading}
              className="login__form-button"
              disabled={authLoading}
            >
              <span>Sign in</span>
              <ArrowIcon />
            </Button>
          </Form.Item>
        </Form>

        <div className="login__divider">
          <span>OR CONTINUE WITH</span>
        </div>

        <div className="login__sso-row">
          <div className="login__sso-provider login__sso-provider--quickbooks">
            <ButtonQuickbooks onClick={handleLoginQuickbooksSSO} />
          </div>
          <div className="login__sso-provider login__sso-provider--google">
            <GoogleLoginButton />
          </div>
        </div>

        <div className="login__demo-card">
          <div className="login__demo-icon" aria-hidden="true">
            D
          </div>
          <div className="login__demo-copy">
            <strong>New to CollectPRO?</strong>
            <span>Try our demo site, no signup needed.</span>
          </div>
          <button type="button" onClick={goToDemo}>
            Demo
            <ArrowIcon />
          </button>
        </div>
      </div>
      <ResetPasswordModal />
    </>
  );
};
